import { useCallback, useRef } from 'react';
import { type Editor } from '@tiptap/react';
import { getAiToolkit } from '@tiptap-pro/ai-toolkit';

import { type Suggestion } from '@/state';
import {
  getBulkSuggestionMutation,
  getContentChangedAfterResolution,
  getNextSuggestionIndex,
  getSuggestionMutation,
} from './suggestion-actions';
import { navigateEditorToSuggestion, trySetEditorSelection } from './suggestion-editor-navigation';

type SuggestionLike = Pick<Suggestion, 'id' | 'metadata'> & {
  range?: { from: number; to: number };
};

// Source - AI Toolkit demo project
// https://template.tiptap.dev/page-ai-toolkit
export const useSuggestionHandlers = <TSuggestion extends SuggestionLike>(
  editor: Editor | null,
  suggestions: TSuggestion[],
  currentIndex: number,
  setSuggestions: (suggestions: TSuggestion[], options?: { batchAcceptedCount?: number }) => void,
  setCurrentIndex: (index: number) => void,
  onAllResolved?: (contentChanged: boolean, batchAcceptedCount: number) => void,
  getSuggestions?: (toolkit: ReturnType<typeof getAiToolkit>) => TSuggestion[],
  acceptedCountBase = 0,
  ensureLiveSuggestions?: (toolkit: ReturnType<typeof getAiToolkit>) => void
) => {
  const hasAnyAcceptRef = useRef(false);
  // Single synchronous accumulator for the review batch's accepted count. It is seeded from the
  // persisted base (already includes prior batch counts, e.g. after a remount) so the count it carries
  // through setSuggestions/onAllResolved is the exact number review-session.ts should persist — there
  // is no second base added downstream. Reset alongside hasAnyAcceptRef when a new batch appears.
  const acceptedCountRef = useRef(acceptedCountBase);
  const prevSuggestionsLengthRef = useRef(0);

  const getCurrentSuggestions = useCallback(
    (toolkit: ReturnType<typeof getAiToolkit>) =>
      getSuggestions?.(toolkit) ?? (toolkit.getSuggestions() as unknown as TSuggestion[]),
    [getSuggestions]
  );

  if (suggestions.length > 0 && prevSuggestionsLengthRef.current === 0) {
    hasAnyAcceptRef.current = false;
    acceptedCountRef.current = acceptedCountBase;
  }
  prevSuggestionsLengthRef.current = suggestions.length;

  const navigateToSuggestion = useCallback(
    (index: number, suggestionsList: TSuggestion[] = suggestions) => {
      navigateEditorToSuggestion({
        editor,
        suggestion: suggestionsList[index],
        index,
        setCurrentIndex,
      });
    },
    [editor, setCurrentIndex, suggestions]
  );

  const updateSuggestions = useCallback(
    (remaining: TSuggestion[], batchAcceptedCount = 0) => {
      if (remaining.length > 0) {
        const newIndex = getNextSuggestionIndex(currentIndex, remaining.length);
        setSuggestions(remaining, { batchAcceptedCount });
        setCurrentIndex(newIndex);
        navigateToSuggestion(newIndex, remaining);
      } else {
        setSuggestions([]);
        setCurrentIndex(0);
      }
    },
    [currentIndex, navigateToSuggestion, setSuggestions, setCurrentIndex]
  );

  const handleAccept = useCallback(() => {
    if (!editor || !suggestions.length) return;

    const toolkit = getAiToolkit(editor);
    ensureLiveSuggestions?.(toolkit);
    const currentSuggestion = suggestions[currentIndex];
    const id = currentSuggestion?.id;
    if (!id) return;

    toolkit[getSuggestionMutation('accept', currentSuggestion)](id);
    const remaining = getCurrentSuggestions(toolkit);
    hasAnyAcceptRef.current = true;
    acceptedCountRef.current += 1;
    updateSuggestions(remaining, acceptedCountRef.current);

    if (remaining.length === 0) {
      trySetEditorSelection(editor, editor.state.selection.to);
      onAllResolved?.(
        getContentChangedAfterResolution({ decision: 'accept', hasAcceptedBefore: hasAnyAcceptRef.current }),
        acceptedCountRef.current
      );
    }
  }, [currentIndex, editor, ensureLiveSuggestions, suggestions, updateSuggestions, onAllResolved, getCurrentSuggestions]);

  const handleReject = useCallback(() => {
    if (!editor || !suggestions.length) return;

    const toolkit = getAiToolkit(editor);
    ensureLiveSuggestions?.(toolkit);
    const currentSuggestion = suggestions[currentIndex];
    const id = currentSuggestion?.id;
    if (!id) return;

    toolkit[getSuggestionMutation('reject', currentSuggestion)](id);
    const remaining = getCurrentSuggestions(toolkit);
    updateSuggestions(remaining, acceptedCountRef.current);

    if (remaining.length === 0) {
      trySetEditorSelection(editor, editor.state.selection.to);
      onAllResolved?.(
        getContentChangedAfterResolution({ decision: 'reject', hasAcceptedBefore: hasAnyAcceptRef.current }),
        acceptedCountRef.current
      );
    }
  }, [currentIndex, editor, ensureLiveSuggestions, suggestions, updateSuggestions, onAllResolved, getCurrentSuggestions]);

  const handleRejectAll = useCallback(() => {
    if (!editor || !suggestions.length) return;

    const toolkit = getAiToolkit(editor);
    ensureLiveSuggestions?.(toolkit);
    const mutation = getBulkSuggestionMutation('reject', suggestions);
    if (!mutation) return;

    suggestions.forEach((suggestion) => {
      toolkit[mutation](suggestion.id);
    });

    setSuggestions([]);
    setCurrentIndex(0);
    trySetEditorSelection(editor, editor.state.selection.to);
    onAllResolved?.(
      getContentChangedAfterResolution({ decision: 'reject', hasAcceptedBefore: hasAnyAcceptRef.current }),
      acceptedCountRef.current
    );
  }, [editor, ensureLiveSuggestions, onAllResolved, setCurrentIndex, setSuggestions, suggestions]);

  const handleAcceptAll = useCallback(() => {
    if (!editor || !suggestions.length) return;

    const toolkit = getAiToolkit(editor);
    ensureLiveSuggestions?.(toolkit);
    const mutation = getBulkSuggestionMutation('accept', suggestions);
    if (!mutation) return;

    acceptedCountRef.current += suggestions.length;
    suggestions.forEach((suggestion) => {
      toolkit[mutation](suggestion.id);
    });

    setSuggestions([]);
    setCurrentIndex(0);
    trySetEditorSelection(editor, editor.state.selection.to);
    hasAnyAcceptRef.current = true;
    onAllResolved?.(
      getContentChangedAfterResolution({ decision: 'accept', hasAcceptedBefore: hasAnyAcceptRef.current }),
      acceptedCountRef.current
    );
  }, [editor, ensureLiveSuggestions, onAllResolved, setCurrentIndex, setSuggestions, suggestions]);

  const handlePrevious = useCallback(
    () => currentIndex > 0 && navigateToSuggestion(currentIndex - 1),
    [currentIndex, navigateToSuggestion]
  );

  const handleNext = useCallback(
    () => currentIndex < suggestions.length - 1 && navigateToSuggestion(currentIndex + 1),
    [currentIndex, suggestions.length, navigateToSuggestion]
  );

  return {
    navigateToSuggestion,
    handleAccept,
    handleReject,
    handleRejectAll,
    handleAcceptAll,
    handlePrevious,
    handleNext,
  };
};
